import { toast } from "@/components/Toast";
import { cloudProjectsManager } from "@/modules/projects/cloudManager";
import { AutoSaveEvent } from "@/modules/projects/hooks/useSaveProjectEvery";
import { localCacheManager } from "@/modules/projects/localManager";
import { cloudProjectActions } from "@/modules/projects/projectProcessor";
import { getProjectLatest } from "@/modules/projects/request";
import { CloudProjectType } from "@/modules/projects/types/cloud";
import events from "@/utils/events";
import { formatTime } from "@/utils/format-time";
import { Button, Flex, Modal, ModalBody, ModalContent, ModalOverlay, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";

const ConflictModal: React.FC = () => {
  const { conflictModalOpen, setConflictModalOpen } = cloudProjectsManager;
  const { id: projectId, updateTime, data: localCache } = localCacheManager;
  const [cloudProjectLatestVersion, setCloudProjectLatestVersion] =
    useState<CloudProjectType | null>(null);

  useEffect(() => {
    if (conflictModalOpen) {
      // 获取 {projectId} 的最新更新时间
      getProjectLatest(projectId).then((res) => {
        const latest = res.data?.project;
        if (latest) {
          setCloudProjectLatestVersion(latest);
        }
      });
    }
  }, [conflictModalOpen]);

  function useCloud() {
    if (!cloudProjectLatestVersion) return;
    cloudProjectActions.useCloudToReplaceLocal(cloudProjectLatestVersion).then((success) => {
      if (success) {
        onSuccess();
      } else {
        toast({
          title: "云端覆盖本地项目的过程遇到了一些问题，请重试或选择别的选项",
          status: "error",
        });
      }
    });
  }

  function useLocal() {
    const { data: localCache } = localCacheManager;

    cloudProjectActions
      .useLocalToReplaceCloud(localCache)
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        toast({
          title: "本地覆盖云端项目的过程遇到了一些问题，请重试或选择别的选项",
          status: "error",
        });
      });
  }

  function keepBoth() {
    const { data: localCache } = localCacheManager;

    cloudProjectActions
      .saveLocalAsNew(localCache)
      .then(() => {
        onSuccess();
      })
      .catch(() => {
        toast({
          title: "保留两者的过程遇到了一些问题，请重试或选择别的选项",
          status: "error",
        });
      });
  }

  function onSuccess() {
    events.emit(AutoSaveEvent.Start);
    setConflictModalOpen(false);
  }

  return (
    <Modal
      size="md"
      isOpen={conflictModalOpen}
      onClose={() => setConflictModalOpen(false)}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
    >
      <ModalOverlay></ModalOverlay>
      <ModalContent rounded="6px" w="600px" maxWidth="600px" bg="bg.modal">
        <ModalBody>
          <Flex flexDirection="column" pt="32px" pb="56px">
            <Text
              color="text.input"
              fontWeight={700}
              textAlign="center"
              w="100%"
              fontSize="18px"
              mb="16px"
            >
              本地项目与云端项目内容冲突
            </Text>
            <Text color="text.title">本地项目更新时间：{formatTime(updateTime)}</Text>
            {cloudProjectLatestVersion ? (
              <Text mt="8px" color="text.title">
                云端项目更新时间：{formatTime(cloudProjectLatestVersion.update_time * 1000)}
              </Text>
            ) : null}

            <Flex mt="32px">
              <Button size="sm" onClick={useCloud} mr="10px" variant="outline">
                使用云端项目（覆盖当前内容）
              </Button>
              <Button size="sm" onClick={useLocal} mr="10px" variant="outline">
                使用本地项目（覆盖云端内容）
              </Button>
              <Button size="sm" onClick={keepBoth} flex={1}>
                保留两者
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const ConflictProcessModal = observer(ConflictModal);
